import React, { useState } from "react";
import { Box, Typography, Chip, Collapse, IconButton } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import ExpandLessIcon from "@material-ui/icons/ExpandLess";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import TuneIcon from "@material-ui/icons/Tune";
import InfoOutlinedIcon from "@material-ui/icons/InfoOutlined";

// ─── Styles ──────────────────────────────────────────────────────────────────

const useStyles = makeStyles(() => ({
  root: {
    background: "#ffffff",
    borderRadius: 12,
    overflow: "hidden",
    minWidth: 300,
  },

  // ── Top header bar ──
  topHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "11px 14px",
    borderBottom: "1px solid #f0f2f7",
    background: "#fafbfd",
  },
  topHeaderLeft: {
    display: "flex",
    alignItems: "center",
    gap: 8,
  },
  tuneIcon: {
    fontSize: 16,
    color: "#64748b",
  },
  topHeaderTitle: {
    fontSize: 14,
    fontWeight: 600,
    color: "#1e293b",
    fontFamily: "inherit",
    letterSpacing: 0.1,
  },
  // ── Section ──
  section: {
    borderBottom: "1px solid #f0f2f7",
    "&:last-child": {
      borderBottom: "none",
    },
  },
  sectionHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "9px 14px",
    cursor: "pointer",
    userSelect: "none",
    transition: "background 0.15s",
    "&:hover": {
      background: "#f8f9fc",
    },
  },
  sectionHeaderLeft: {
    display: "flex",
    alignItems: "center",
    gap: 8,
  },
  sectionDot: {
    width: 8,
    height: 8,
    borderRadius: "50%",
    flexShrink: 0,
  },
  sectionTitle: {
    fontSize: 12.5,
    fontFamily: "inherit",
    letterSpacing: 0.2,
    textTransform: "none",
  },
  sectionHeaderRight: {
    display: "flex",
    alignItems: "center",
    gap: 6,
  },
  countBadge: {
    minWidth: 20,
    height: 20,
    borderRadius: 10,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 11,
    fontFamily: "inherit",
    padding: "0 6px",
  },
  chevronBtn: {
    padding: 2,
    color: "#94a3b8",
    "&:hover": { background: "transparent", color: "#64748b" },
  },
  chevronIcon: {
    fontSize: 16,
  },

  // ── Rows ──
  rowsContainer: {
    background: "#ffffff",
  },
  row: {
    display: "flex",
    alignItems: "flex-start",
    padding: "8px 14px 8px 30px",
    borderTop: "1px solid #f5f6fa",
    gap: 12,
    "&:first-child": {
      borderTop: "none",
    },
  },
  rowKey: {
    fontSize: 12.5,
    fontWeight: 450,
    color: "#4d6280",
    fontFamily: "inherit",
    minWidth: 100,
    flexShrink: 0,
    lineHeight: 1.5,
    paddingTop: 1,
    maxWidth: 150,
    width: 150
  },
  rowValue: {
    fontSize: 12.5,
    color: "#1e293b",
    fontFamily: "inherit",
    lineHeight: 1.5,
    flex: 1,
  },
  rowValueItalic: {
    fontStyle: "italic",
    color: "#64748b",
    fontWeight: 400,
  },
  assumedChip: {
    fontSize: 10,
    height: 18,
    background: "#f1f5f9",
    color: "#64748b !important",
    border: "1px solid #e2e8f0",
    borderRadius: 100,
    fontFamily: "inherit",
    marginLeft: 6,
    flexShrink: 0,
    // "& .MuiChip-label": { padding: "0 6px" },
  },

  // ── Footer ──
  footer: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    padding: "9px 14px",
    borderTop: "1px solid #f0f2f7",
    background: "#fafbfd",
  },
  footerIcon: {
    fontSize: 14,
    color: "#94a3b8",
  },
  footerText: {
    fontSize: 11,
    color: "#94a3b8",
    fontFamily: "inherit",
    lineHeight: 1.4,
  },
  footerLink: {
    color: "#3B82F6",
    fontWeight: 600,
    cursor: "pointer",
    textDecoration: "underline",
    textUnderlineOffset: 2,
    "&:hover": {
      color: "#1d4ed8",
    },
  },
}));

// ─── Section colours ──────────────────────────────────────────────────────────

const SECTION_STYLES = {
  prompt: {
    dot: "#3B82F6",
    title: "#3B82F6",
    badge: { background: "#dbeafe", color: "#1d4ed8" },
  },
  answers: {
    dot: "#22c55e",
    title: "#16a34a",
    badge: { background: "#dcfce7", color: "#15803d" },
  },
  assumed: {
    dot: "#cbd5e1",
    title: "#94a3b8",
    badge: { background: "#f1f5f9", color: "#64748b" },
  },
};

// ─── SummarySection ───────────────────────────────────────────────────────────

const SummarySection = ({ section }) => {
  const classes = useStyles();
  const [open, setOpen] = useState(true);
  const style = SECTION_STYLES[section.type] || SECTION_STYLES.prompt;

  return (
    <Box className={classes.section}>
      {/* Section header — accordion trigger */}
      <Box className={classes.sectionHeader} onClick={() => setOpen((o) => !o)}>
        <Box className={classes.sectionHeaderLeft}>
          <Box
            className={classes.sectionDot}
            style={{ background: style.dot }}
          />
          <Typography
            className={classes.sectionTitle}
            style={{ color: style.title }}
          >
            {section.label}
          </Typography>
        </Box>
        <Box className={classes.sectionHeaderRight}>
          <Box
            className={classes.countBadge}
            style={style.badge}
          >
            {section.rows.length}
          </Box>
          <IconButton className={classes.chevronBtn} size="small" disableRipple>
            {open
              ? <ExpandLessIcon className={classes.chevronIcon} />
              : <ExpandMoreIcon className={classes.chevronIcon} />
            }
          </IconButton>
        </Box>
      </Box>

      {/* Rows */}
      <Collapse in={open}>
        <Box className={classes.rowsContainer}>
          {section.rows.map((row, i) => (
            <Box key={i} className={classes.row}>
              <Typography className={classes.rowKey}>{row.key}</Typography>
              <Typography
                className={`${classes.rowValue} ${row.assumed ? classes.rowValueItalic : ""}`}
              >
                {row.value}
              </Typography>
              {row.assumed && (
                <Chip label="assumed" size="small" className={classes.assumedChip} />
              )}
            </Box>
          ))}
        </Box>
      </Collapse>
    </Box>
  );
};

// ─── GenerationSummary ────────────────────────────────────────────────────────

/**
 * Props:
 *  title       {string}           – Top header title text
 *  isGenerating {boolean}         – Show the spinning "Generating…" indicator
 *  sections    {Array}            – Array of section objects (see shape below)
 *  footerNote  {string}           – Footer hint text
 *
 * Section shape:
 *  {
 *    type:  "prompt" | "answers" | "assumed"   // controls colour
 *    label: string                              // e.g. "From your prompt"
 *    rows:  Array<{ key: string, value: string, assumed?: boolean }>
 *  }
 */
const GenerationSummary = ({
  title = "Here's what I understood — generating now",
  isGenerating,
  sections = [
    {
      type: "prompt",
      label: "From your input",
      rows: [
      ],
    },
    {
      type: "answers",
      label: "From your answers",
      rows: [
      ],
    },
    {
      type: "assumed",
      label: "Assumed by AI",
      rows: [
      ],
    },
  ],
  footerNote = "Not what you expected? You can regenerate specific sections after generation completes.",
  onStopGeneration
}) => {
  const classes = useStyles();
  const [stopped, setStopped] = useState(false);

  const handleStopGeneration = () => {
    setStopped(true);
    onStopGeneration();
  }

  return (
    <Box className={classes.root}>
      {/* Top header */}
      <Box className={classes.topHeader}>
        <Box className={classes.topHeaderLeft}>
          <TuneIcon className={classes.tuneIcon} />
          <Typography className={classes.topHeaderTitle}>{title}</Typography>
        </Box>
      </Box>

      {/* Accordion sections */}
      {sections.map((section, i) => (
        <SummarySection key={i} section={section} />
      ))}

      {/* Footer */}
      {footerNote && (
        <Box className={classes.footer}>
          <InfoOutlinedIcon className={classes.footerIcon} />
          {isGenerating && !stopped ? <Typography className={classes.footerText}>
              Not what you expected?{" "}
              <span
                className={classes.footerLink}
                role="button"
                tabIndex={0}
                onClick={handleStopGeneration}
                // onKeyDown={(e) => {
                //   if (e.key === "Enter" || e.key === " ") {
                //     e.preventDefault();
                //     onStopGeneration();
                //   }
                // }}
              >
                Stop now
              </span>{" "}
              or you can regenerate specific sections after generation completes.
            </Typography> 
            :
            <Typography className={classes.footerText}>{footerNote}</Typography>
            }
          
        </Box>
      )}
    </Box>
  );
};

export default GenerationSummary;